"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const TextField_1 = __importDefault(require("@material-ui/core/TextField"));
const Autocomplete_1 = __importDefault(require("@material-ui/lab/Autocomplete"));
const Link_1 = __importDefault(require("@material-ui/core/Link"));
const Search_1 = __importDefault(require("@material-ui/icons/Search"));
const core_1 = require("@material-ui/core");
const uuid_1 = require("uuid");
const Checkbox_1 = __importDefault(require("../Checkbox"));
const LoadingIndicator_1 = __importDefault(require("../LoadingIndicator"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const Stack_1 = __importDefault(require("../../layouts/Stack"));
const TokenGroup_1 = __importDefault(require("../TokenGroup"));
const getFlattenOptions_1 = require("../../utils/getFlattenOptions");
const useStyles = core_1.makeStyles((theme) => ({
    input: {
        display: 'inline-block',
        marginRight: '20px',
    },
    recoveryLink: {
        pointerEvents: 'auto',
    },
    textfield: {
        paddingRight: '30px',
    },
}));
/** The multiselect is a normal text input enhanced by a panel of suggested options. */
const Multiselect = ({ options = [], value, filteringType = 'auto', loadingText = 'Loading ...', errorText = 'Error fetching', recoveryText, name, controlId = uuid_1.v4(), empty, disabled, placeholder, invalid, checkboxes = false, freeSolo = false, ariaRequired = false, ariaDescribedby, ariaLabelledby, onChange, onInputChange, onRecoveryClick, onFocus, onBlur, disableBrowserAutocorrect = false, statusType = 'finished', }) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = react_1.useState([]);
    const [open, setOpen] = react_1.default.useState(false);
    const autoCompleteString = react_1.useMemo(() => (disableBrowserAutocorrect ? 'off' : 'on'), [disableBrowserAutocorrect]);
    react_1.useEffect(() => {
        setInputValue(value || []);
    }, [value, setInputValue]);
    const onRecoveryClickHandler = react_1.useCallback((event) => {
        event.preventDefault();
        onRecoveryClick === null || onRecoveryClick === void 0 ? void 0 : onRecoveryClick(event);
        event.stopPropagation();
    }, [onRecoveryClick]);
    const flattenOptions = react_1.useMemo(() => {
        return getFlattenOptions_1.getFlattenOptions(options);
    }, [options]);
    const handleOnChange = react_1.useCallback((event, values) => {
        const valuesAsOptions = (values || []).map((value) => typeof value === 'string' ? { value, label: value } : value);
        onChange === null || onChange === void 0 ? void 0 : onChange(valuesAsOptions);
        setInputValue(valuesAsOptions);
    }, [onChange, setInputValue]);
    const handleOnInput = react_1.useCallback((event, value, reason) => {
        if (filteringType === 'manual') {
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(event, value, reason);
        }
    }, [onInputChange, filteringType]);
    const loadingAndErrorText = react_1.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null,
        statusType === 'loading' && react_1.default.createElement(LoadingIndicator_1.default, { label: loadingText }),
        statusType === 'error' && (react_1.default.createElement(StatusIndicator_1.default, { statusType: "negative" },
            errorText,
            ".",
            recoveryText && (react_1.default.createElement("span", null,
                react_1.default.createElement(Link_1.default, { href: "#", onClick: onRecoveryClickHandler, className: classes.recoveryLink }, recoveryText))))))), [statusType, loadingText, errorText, recoveryText, onRecoveryClickHandler, classes]);
    const textfield = react_1.useCallback((params) => (react_1.default.createElement(TextField_1.default, Object.assign({ autoCorrect: autoCompleteString, placeholder: placeholder, required: ariaRequired, error: !!(statusType === 'error' || invalid) }, params, { variant: "outlined", size: "small", margin: "normal", name: name, InputProps: Object.assign(Object.assign({ 'aria-required': ariaRequired, 'aria-describedby': ariaDescribedby, 'aria-labelledby': ariaLabelledby }, params.InputProps), { className: classes.textfield, type: 'search', startAdornment: (react_1.default.createElement(core_1.InputAdornment, { position: "start" },
                react_1.default.createElement(Search_1.default, { color: "action" }))) }) }))), [
        autoCompleteString,
        placeholder,
        statusType,
        name,
        invalid,
        ariaRequired,
        ariaDescribedby,
        ariaLabelledby,
        classes.textfield,
    ]);
    const renderOption = react_1.useCallback((option) => {
        return checkboxes ? (react_1.default.createElement(Checkbox_1.default, { checked: inputValue.map((input) => input.value).includes(option.value), value: option.value }, option.label)) : (option.label);
    }, [checkboxes, inputValue]);
    const handleDeleteOption = react_1.useCallback((option) => {
        const tempData = inputValue.filter((o) => o.value !== option.value);
        setInputValue(tempData);
        onChange === null || onChange === void 0 ? void 0 : onChange(tempData);
    }, [inputValue, setInputValue, onChange]);
    return (react_1.default.createElement(Stack_1.default, null,
        react_1.default.createElement(Autocomplete_1.default, { multiple: true, disabled: disabled, autoHighlight: true, popupIcon: null, id: controlId, freeSolo: freeSolo, noOptionsText: empty, value: inputValue, getOptionSelected: (opt, v) => opt.value === v.value, open: statusType === 'error' || statusType === 'loading' ? true : open, options: flattenOptions, loadingText: loadingAndErrorText, onChange: handleOnChange, onInputChange: handleOnInput, loading: statusType !== 'finished', renderInput: textfield, renderOption: renderOption, onBlur: onBlur, onFocus: onFocus, onOpen: () => setOpen(true), onClose: () => setOpen(false), getOptionLabel: (option) => option.label || '', "data-testid": "multiselect" }),
        react_1.default.createElement(TokenGroup_1.default, { items: inputValue || [], onDismiss: handleDeleteOption })));
};
exports.default = Multiselect;
//# sourceMappingURL=index.js.map